import React, { useState } from 'react';
import Modal from '../Common/Modal';
import { formatISTDateOnly } from '../../utils/dateUtils';

// Small copy-to-clipboard button with transient "Copied" feedback.
const CopyButton = ({ text, label = '' }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Fallback for non-secure contexts / older browsers
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch { /* noop */ }
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      onClick={handleCopy}
      title={`Copy ${label}`.trim()}
      className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-gray-700 hover:bg-gray-600 text-gray-200 transition-colors shrink-0"
    >
      {copied ? (
        <>
          <svg className="w-3.5 h-3.5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
          Copied
        </>
      ) : (
        <>
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
          Copy
        </>
      )}
    </button>
  );
};

const ALL_FIELDS = [
  { id: 'article', label: 'Article' },
  { id: 'quantity', label: 'Quantity' },
  { id: 'denomination', label: 'Denomination' },
  { id: 'consideration_amount', label: 'Consideration Amount' },
  { id: 'delivery_mode', label: 'Delivery Mode' },
  { id: 'delivery_address', label: 'Delivery Address' },
  { id: 'property_description', label: 'Property Description' },
  { id: 'notes', label: 'Notes' },
  { id: 'stamp_purchased_by', label: 'Purchased By' },
  { id: 'stamp_paid_by', label: 'Paid By' },
  { id: 'first_party_name', label: 'First Party Name' },
  { id: 'first_party_address', label: 'First Party Address' },
  { id: 'first_party_mobile', label: 'First Party Mobile' },
  { id: 'second_party_name', label: 'Second Party Name' },
  { id: 'second_party_address', label: 'Second Party Address' },
  { id: 'second_party_mobile', label: 'Second Party Mobile' },
];

const LiveStampingServiceDetailModal = ({ service, orders, onClose, onViewOrder, onEdit, onDelete, onRequest }) => {
  const [activeTab, setActiveTab] = useState('info'); // 'info' | 'requests' | 'api'

  if (!service) return null;

  const serviceOrders = orders.filter(o => (o.service_id || o.serviceId) === service.id);
  const serviceStamps = service.stamps || [{ id: 1, locked_fields: {}, default_values: {} }];

  const demoPayload = {
    service_id: service.id,
    stamp_data: serviceStamps.map(stamp => {
      const locked = stamp.locked_fields || stamp.lockedFields || {};
      const stampPayload = {
        stamp_index: stamp.id,
        article: locked.article ? undefined : '<Article Name>',
        denomination: locked.denomination ? undefined : 100,
        quantity: locked.quantity ? undefined : 1,
        stamp_purchased_by: locked.stamp_purchased_by ? undefined : '<Purchased By Name>',
        stamp_paid_by: locked.stamp_paid_by ? undefined : '<Paid By Name>',
        consideration_amount: locked.consideration_amount ? undefined : 500000,
        first_party: locked.first_party_name ? undefined : {
          name: '<Name>', address: '<Address>', mobile: '<Mobile>'
        },
        second_party: locked.second_party_name ? undefined : {
          name: '<Name>', address: '<Address>', mobile: '<Mobile>'
        },
        property_description: locked.property_description ? undefined : '<Property Description>',
        notes: locked.notes ? undefined : '<Notes>'
      };
      return Object.fromEntries(Object.entries(stampPayload).filter(([_, v]) => v !== undefined));
    }),
    delivery_mode: 'digital',
    external_ref_id: 'YOUR_REF_123'
  };

  const getStatusColor = (status) => {
    const colors = {
      placed: 'bg-blue-100 text-blue-800',
      executing: 'bg-orange-100 text-orange-800',
      completed: 'bg-green-100 text-green-800',
      ops_review: 'bg-yellow-100 text-yellow-800',
      rejected: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const tabs = [
    { id: 'info', label: 'Service Info' },
    { id: 'requests', label: 'Requests', count: serviceOrders.length },
    { id: 'api', label: 'API Integration' },
  ];

  return (
    <Modal isOpen={!!service} onClose={onClose}>
      <div className="flex flex-col h-full">

        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-white flex-shrink-0">
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="p-1.5 -ml-1 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-all focus:outline-none"
              aria-label="Back"
              title="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <div>
              <h2 className="text-xl font-bold text-gray-900">{service.name}</h2>
              <p className="text-gray-500 text-sm mt-1">{service.state} &bull; Stamp duty &#8377;{(service.stamps?.reduce((sum, st) => sum + ((st.default_values?.denomination || 0) * (st.default_values?.quantity || 1)), 0) || 0).toLocaleString('en-IN')}</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 bg-gray-50 flex-shrink-0">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 text-sm font-medium flex items-center gap-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-b-2 border-indigo-600 text-indigo-600 bg-white'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
              {tab.count !== undefined && (
                <span className="bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full text-xs font-semibold">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">

          {/* ── SERVICE INFO ── */}
          {activeTab === 'info' && (
            <div className="space-y-6">
              <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-100">
                <h4 className="font-bold text-indigo-800 mb-3">Service Information</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-indigo-600 block text-xs mb-0.5">Service ID</span>
                    <span className="font-mono text-indigo-900 text-sm">{service.id}</span>
                  </div>
                  <div>
                    <span className="text-indigo-600 block text-xs mb-0.5">State</span>
                    <span className="text-indigo-900">{service.state}</span>
                  </div>
                  <div>
                    <span className="text-indigo-600 block text-xs mb-0.5">Stamp Papers</span>
                    <span className="text-indigo-900 font-semibold">{serviceStamps.length}</span>
                  </div>
                </div>
              </div>

              {serviceStamps.map(stamp => {
                const lockedFields = stamp.locked_fields || stamp.lockedFields || {};
                const defaults = stamp.defaultValues || stamp.default_values || {};
                const lockedKeys = Object.keys(lockedFields).filter(k => lockedFields[k]);

                return (
                  <div key={stamp.id} className="border border-gray-200 rounded-xl overflow-hidden">
                    <div className="bg-gray-50 px-4 py-2.5 border-b border-gray-200 flex justify-between items-center">
                      <h4 className="font-semibold text-gray-700 text-sm">Stamp #{stamp.id} Configuration</h4>
                      <span className="text-xs text-gray-400">{defaults.article || 'Generic Stamp'}</span>
                    </div>
                    {(() => {
                      // Prefer the API's derived `unlocked` breakdown (present for all callers);
                      // fall back to raw required_fields (admin-only) for older/cached payloads.
                      const fsu = stamp.unlocked;
                      const toItems = ids => (ids || []).map(id => ALL_FIELDS.find(f => f.id === id) || { id, label: id });
                      let requiredItems, optionalItems;
                      if (fsu) {
                        requiredItems = toItems(fsu.required);
                        optionalItems = toItems(fsu.optional);
                      } else {
                        const requiredCfg = stamp.required_fields || stamp.requiredFields || {};
                        requiredItems = ALL_FIELDS.filter(f => !lockedFields[f.id] && requiredCfg[f.id]);
                        optionalItems = ALL_FIELDS.filter(f => !lockedFields[f.id] && !requiredCfg[f.id]);
                      }
                      // One "Unlocked Fields" list: required ones first (tagged), then optional.
                      const unlockedItems = [
                        ...requiredItems.map(f => ({ ...f, required: true })),
                        ...optionalItems.map(f => ({ ...f, required: false })),
                      ];
                      return (
                        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Locked / pre-filled */}
                          <div className="bg-green-50 rounded-lg p-3 border border-green-100">
                            <div className="text-xs font-bold text-green-800 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                              Fixed (Pre-filled)
                              <span className="bg-green-200 text-green-800 px-1.5 rounded-full text-[10px]">{lockedKeys.length}</span>
                            </div>
                            {lockedKeys.length > 0 ? (
                              <ul className="text-sm space-y-1">
                                {lockedKeys.map(key => (
                                  <li key={key} className="flex justify-between gap-2">
                                    <span className="text-gray-500">{ALL_FIELDS.find(f => f.id === key)?.label || key}</span>
                                    <span className="font-mono font-semibold text-green-700 text-right">
                                      {typeof defaults[key] === 'object' ? 'Configured' : (defaults[key] || '—')}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <span className="text-xs text-gray-400 italic">No fixed fields</span>
                            )}
                          </div>

                          {/* Unlocked — client fills these. Tagged = required, untagged = optional. */}
                          <div className="bg-orange-50 rounded-lg p-3 border border-orange-100">
                            <div className="text-xs font-bold text-orange-800 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                              Unlocked Fields
                              <span className="bg-orange-200 text-orange-800 px-1.5 rounded-full text-[10px]">{unlockedItems.length}</span>
                            </div>
                            {unlockedItems.length > 0 ? (
                              <ul className="text-sm space-y-1 text-gray-700">
                                {unlockedItems.map(f => (
                                  <li key={f.id} className="flex items-center justify-between gap-2">
                                    <span>{f.label}</span>
                                    {f.required && (
                                      <span className="bg-red-100 text-red-700 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide shrink-0">Required</span>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <span className="text-xs text-gray-400 italic">All fields pre-filled</span>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                );
              })}
            </div>
          )}

          {/* ── REQUESTS ── */}
          {activeTab === 'requests' && (
            <div>
              {serviceOrders.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <p className="text-base mb-1">No requests yet for this service</p>
                  <p className="text-sm">Orders placed via the form or API will appear here</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                      <tr>
                        <th className="px-4 py-3 text-left">Request ID</th>
                        <th className="px-4 py-3 text-left">First Party</th>
                        <th className="px-4 py-3 text-left">Amount</th>
                        <th className="px-4 py-3 text-left">Status</th>
                        <th className="px-4 py-3 text-left">Date</th>
                        <th className="px-4 py-3 text-center">View</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {serviceOrders.map(order => (
                        <tr key={order.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-indigo-600">{order.id}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{order.first_party?.name || '-'}</td>
                          <td className="px-4 py-3 text-sm font-medium">&#8377;{order.amount}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                              {order.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {formatISTDateOnly(order.created_at)}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => onViewOrder(order)}
                              className="text-indigo-600 hover:text-indigo-800"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ── API INTEGRATION ── */}
          {activeTab === 'api' && (
            <div className="space-y-5">
              <p className="text-sm text-gray-500">
                Use the endpoint below to place orders for this service programmatically. The request body is pre-populated with only the fields this service requires — locked fields are excluded.
              </p>

              <div className="bg-gray-900 rounded-xl p-4 text-white">
                <div className="bg-gray-800 rounded-lg p-3 mb-4">
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <span className="text-green-400 font-bold text-xs uppercase tracking-wide">POST</span>
                    <CopyButton text="/api/external/v1/live-stamping/request" label="endpoint" />
                  </div>
                  <code className="text-yellow-300 text-sm break-all">/api/external/v1/live-stamping/request</code>
                </div>

                <div className="bg-gray-800 rounded-lg p-3 mb-4">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide">Headers</p>
                    <CopyButton text={'X-API-Key: <your_api_key>\nContent-Type: application/json'} label="headers" />
                  </div>
                  <code className="text-yellow-300 text-sm block">X-API-Key: &lt;your_api_key&gt;</code>
                  <code className="text-yellow-300 text-sm block">Content-Type: application/json</code>
                </div>

                <div className="bg-gray-800 rounded-lg p-3">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide">Request Body</p>
                    <CopyButton text={JSON.stringify(demoPayload, null, 2)} label="request body" />
                  </div>
                  <pre className="text-green-300 text-xs overflow-x-auto leading-relaxed">
                    {JSON.stringify(demoPayload, null, 2)}
                  </pre>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-sm text-blue-800">
                <p className="font-semibold mb-1">Idempotency</p>
                <p className="text-blue-700 text-sm">Pass a unique <code className="bg-blue-100 px-1 rounded text-xs">external_ref_id</code> per order. Re-submitting the same value returns the existing order without creating a duplicate or charging the wallet again.</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-3 flex-shrink-0">
          {onDelete && (
            <button
              onClick={() => {
                if (window.confirm('Are you sure you want to delete this service?')) {
                  onDelete(service.id);
                  onClose();
                }
              }}
              className="px-5 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 font-medium flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete Service
            </button>
          )}
          {onEdit && (
            <button
              onClick={() => { onEdit(service); onClose(); }}
              className="px-5 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 font-medium flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Edit Service
            </button>
          )}
          {onRequest && (service.isEnabled || service.is_enabled) && process.env.REACT_APP_HIDE_LIVE_STAMPING_PLACE_ORDER !== 'true' && (
            <button
              onClick={() => { onRequest(service); onClose(); }}
              className="px-5 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Place Order
            </button>
          )}
          <button
            onClick={onClose}
            className="px-5 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default LiveStampingServiceDetailModal;
